import {
  Alert,
  Box,
  Button,
  Container,
  LinearProgress,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { PollResponse, ResponseValue, Voter } from '../../types';
import React, { useRef, useState } from 'react';
import { SearchParams, searchIndex, splitKeywords } from '../../utils/indexer';
import { Timestamp, collection, doc, getDoc, setDoc } from 'firebase/firestore';

import { GoogleGenerativeAI } from '@google/generative-ai';
import { db } from '../../firebase';
import { useAuth } from '../../hooks/useAuth';
import { v4 as uuidv4 } from 'uuid';

interface Responses {
  [question: string]: ResponseValue;
}

interface VoterData {
  number: string;
  name: string;
  indicator: 'SO' | 'A' | 'PDS5' | 'PDS1' | 'U-A';
  sourceFile: string;
}

const PilarBatchPoll: React.FC = () => {
  const { user, loading } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [processedFiles, setProcessedFiles] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);
  const [voterData, setVoterData] = useState<VoterData[]>([]);

  const [failedVoterImport, setFailedVoterImport] = useState<string[]>([]);
  const [polledVoters, setPolledVoters] = useState(0);

  const dashboardId = 'SURIGAO DEL NORTE_DISTRICT 1';

  // Gemini API setup (replace with your actual API key)
  const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY ?? 'AIzaSyBmmS-_40L0zrMqQe0jq5vaojr-VGUp6vU');

  const processImage = async (imageDataUrl: string, fileName: string) => {
    try {
      const model = genAI.getGenerativeModel({ model: "gemini-2.5-flash-preview-04-17" });
      
      const base64Image = imageDataUrl.split(',')[1];
      
      const prompt = `
        Extract voter information from this document. 
        For each row, provide:
        - Voter Number
        - Voter Name
        - Voter Indicator (A 1 to 4 character code. Examples: SO, A, PDS5, PDS1, or U-A)
        
        Return the data in a JSON array format:
        [
          {
            "number": "string",
            "name": "string", 
            "indicator": "string"
          }
        ]
      `;

      const result = await model.generateContent({
        contents: [{ role: "user", parts: [
          { text: prompt },
          { inlineData: { mimeType: "image/jpeg", data: base64Image } }
        ]}]
      });

      const response = result.response;
      const text = response.text();
      
      // Remove code block markers and potential whitespace
      const cleanedText = text.replace(/^```(json)?/m, '').replace(/```$/, '').trim();
      
      const parsedData: Omit<VoterData, 'sourceFile'>[] = JSON.parse(cleanedText);
      
      // Add sourceFile to each voter data entry
      const votersWithSourceFile: VoterData[] = parsedData.map(voter => ({
        ...voter,
        sourceFile: fileName
      }));
      
      setVoterData((prevData) => [...prevData, ...votersWithSourceFile]);
      setProcessedFiles(prev => prev + 1);
    } catch (error) {
      console.error('Image processing error:', error);
      setSnackbarMessage(`Failed to process image`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const readFileAsDataURL = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        resolve(result);
      };
      reader.onerror = (e) => {
        reject(new Error('File reading error'));
      };
      reader.readAsDataURL(file);
    });
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      // Convert FileList to array and sort by filename
      const sortedFiles = Array.from(files).sort((a, b) => a.name.localeCompare(b.name));
      
      setTotalFiles(sortedFiles.length);
      setProcessedFiles(0);
      setIsProcessing(true);
      
      // Process files sequentially in sorted order
      for (const file of sortedFiles) {
        try {
          const imageDataUrl = await readFileAsDataURL(file);
          await processImage(imageDataUrl, file.name);
        } catch (error) {
          console.error('Error processing file:', file.name, error);
          setSnackbarMessage(`Failed to process file: ${file.name}`);
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        }
      }
      
      setIsProcessing(false);
      
      // Show snackbar when all images are processed
      setSnackbarMessage(`Successfully processed ${sortedFiles.length} image(s)`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const submitPollResponse = async (responses: Responses): Promise<PollResponse | null> => {
    try {
      const responseId = uuidv4();
      const pollResponse: PollResponse = {
        id: responseId,
        pollId: "FYIo2XxC0ZxeBPX2kgGG",
        pollTitle: "Pilar (VIS)",
        responses,
        submittedAt: Timestamp.now(),
        submittedBy: user?.display_name ?? 'Keeta Developer',
      };
      
      const responseRef = doc(collection(db, 'pollResponses'), responseId);
      await setDoc(responseRef, pollResponse);
      
      return pollResponse;
    } catch (error) {
      console.error('Error submitting poll response: ', error);
    }
    
    return null;
  };
  
  const searchVoter = async (keyword: string, municipality: string): Promise<Voter | null> => {
    const keywords = splitKeywords(keyword);
    const searchParams: SearchParams = {
      keywords,
      resultLimit: 10,
      document: "voter",
      field: "name",
      searchAfter: null
    };
    let result = await searchIndex(searchParams);
    while ((result.indexes.length <= 0) && (keywords.length > 2)) {
      keywords.pop();
      searchParams.keywords = keywords;
      result = await searchIndex(searchParams);
    }
    
    for (const index of result.indexes) {
      const voterDocRef = doc(db, index.documentPath);
      const voterDoc = await getDoc(voterDocRef);
      if (voterDoc.exists()) {
        const voter = voterDoc.data() as Voter;
        if (voter.city_municipality === municipality) {
          return voter;
        }
      }
    }

    return null;
  };

  const wasPolledBefore = async (voter: Voter): Promise<boolean> => {
    try {
      const latestPollResponsePath = 
        `tabulationsV2/${dashboardId}/regions/${voter.region}/`
        + `provinces/${voter.province}/districts/${voter.district}/`
        + `cities/${voter.city_municipality}/barangays/${voter.barangay}/`
        + `precincts/${voter.precinct}/voters/${voter.name}`;

      const latestPollResponseRef = doc(db, latestPollResponsePath);
      const latestPollResponseDoc = await getDoc(latestPollResponseRef);
      return latestPollResponseDoc.exists();
    } catch (error) {
      console.error('Error checking if voter was polled before: ', error);
      return false;
    }
  };

  const pollVoter = async (voterData: VoterData): Promise<boolean> => {
    const voter = await searchVoter(voterData.name, "PILAR");
    if (voter) {
      const abante: Responses = {
        "DISTRICT 1 REPRESENTATIVE": "Lucille Sering",
        "MAYOR": "April Zerda",
        "PARTY": "Abante Surigao",
        "PROVINCIAL GOVERNOR": "Lyndon Barbers",
        "PROVINCIAL VICE GOVERNOR": "Geed Gokiangkee",
        "VOTER": voter.name,
      };
      const wasPolled = await wasPolledBefore(voter);
      if (!wasPolled) {
        const response = await submitPollResponse(abante);
        console.log(`Response submitted: ${response?.id}, Voter: ${voter.name}`);
        return true;
      } else {
        console.log(`Voter already polled: ${voter.name}`);
      }
    } else {
      console.error(`Voter not found in database: ${voterData.name}`);
      setFailedVoterImport((prev) => [...prev, voterData.name]);
    }

    return false;
  };

  const startPoll = async () => {
    setIsPolling(true);
    setPolledVoters(0);
    try {
      for (let i = 0; i < voterData.length; i++) {
        const vd = voterData[i];
        const polled = await pollVoter(vd);
        if (polled) {
          const pilarBatchPollRef = collection(db, 'pilarBatchPoll');
          const pilarBatchPollDocRef = doc(pilarBatchPollRef);
          await setDoc(pilarBatchPollDocRef, {
            name: vd.name,
            number: vd.number,
            indicator: vd.indicator,
            submittedAt: Timestamp.now(),
            sourceFile: vd.sourceFile || 'Unknown'
          });
        }

        setPolledVoters(prev => prev + 1);
      }
      
      // Show success snackbar when polling is complete
      setSnackbarMessage('Polling completed successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error during polling:', error);
      setSnackbarMessage('Polling encountered an error');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsPolling(false);
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ position: 'relative' }}>
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" gutterBottom>
          Pilar Batch Poll
        </Typography>
        
        {isProcessing && (
          <Box 
            sx={{
              alignItems: 'center', 
              p: 2
            }}
          >
            <Typography variant="body1" sx={{ mb: 2 }}>
              Processing Images: {processedFiles} / {totalFiles}
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={(processedFiles / totalFiles) * 100} 
              sx={{ width: '100%' }} 
            />
          </Box>
        )}
                
        {/* File Input for Multiple Images */}
        <input 
          type="file" 
          ref={fileInputRef}
          multiple 
          accept="image/*" 
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
        <Button 
          variant="contained" 
          onClick={() => fileInputRef.current?.click()}
          sx={{ mb: 2 }}
        >
          Select Images
        </Button>

        {/* Voter Data Table */}
        {voterData.length > 0 && (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Voter Number</TableCell>
                    <TableCell>Voter Name</TableCell>
                    <TableCell>Indicator</TableCell>
                    <TableCell>Source File</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {voterData.map((voter, index) => (
                    <TableRow key={index}>
                      <TableCell>{voter.number}</TableCell>
                      <TableCell>{voter.name}</TableCell>
                      <TableCell>{voter.indicator}</TableCell>
                      <TableCell>{voter.sourceFile}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Linear Progress for Polling */}
            <Box sx={{ my: 2 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Polling Progress: {polledVoters} / {voterData.length}
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={(polledVoters / voterData.length) * 100} 
                sx={{ width: '100%' }} 
              />
            </Box>
            
            <Button 
              variant="contained" 
              onClick={startPoll}
              disabled={isProcessing || isPolling}
            >
              {isPolling ? 'Polling...' : 'Start Poll'}
            </Button>
          </>
        )}

        {/* Failed Voter Import List */}
        {failedVoterImport.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Failed Voter Imports
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Voter Name</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {failedVoterImport.map((name, index) => (
                    <TableRow key={index}>
                      <TableCell>{name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Box>

      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity} 
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PilarBatchPoll;
